import { Linking, Platform } from 'react-native'

import { useNavigation } from '@react-navigation/native'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@zet/common/components/toast'
import { getSessionData } from '@zet/common/utils/getSessionData'
import { i18n } from '@zet/localization'
import { getTagType, replaceLoginHash } from '@zet/utils/loginHash'
import {
  getUserFlowNextRoute,
  isUserFlowActive,
  setUserFlowRoute,
  setUserFlowSession,
} from '@zet/utils/userFlowSession'

import useStore from '../../../store'
import useAddCustomerStore from '../../../store/addCustomer'
import useMyCustomersStore from '../../../store/myCustomers'
import { states, webStates } from '../../../utils/addCustomerStates'
import { getNextStateAddCustomerState } from '../../functions/customerAdditionStateMachine'
import { useCustomerNavigation } from './useCustomerNavigation'

const useAddCustomerNextState = () => {
  const {
    setCustomerId,
    customerId: addCustomerId,
    setIsReconcentFlowOtpVerify,
    isReconcentFlowOtpVerify,
  } = useAddCustomerStore((store) => store)
  const { isBrandsFlow, partnerId } = useMyCustomersStore((state) => state)
  const selectedCustomer = useStore((state) => state.selectedCustomer)
  const queryClient = useQueryClient()
  const navigation = useNavigation()
  const toast = useToast()
  const { navigateToCustomerFlow } = useCustomerNavigation()
  return useMutation(getNextStateAddCustomerState, {
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['home-widgets'] })
      queryClient.invalidateQueries(['userCustomerCount'])
      queryClient.invalidateQueries(['CustomerCount'])
      queryClient.invalidateQueries(['visitorActivitiesList'])
      if (data?.actionData?.error?.code === 2025) {
        toast?.show({
          intent: 'error',
          heading: i18n.t('toastMessages.error'),
          description:
            data?.actionData?.error?.message ||
            i18n.t('toastMessages.somethingWentWrong'),
          hideAfter: 3000,
        })
      }

      if (data?.code === 400 && data?.message === 'CAN_NOT_GO_BACK') {
        if (variables.isDemoSale) {
          navigation.replace('HomeView')
        } else {
          navigation.goBack()
        }
        return data
      }

      if (
        data?.code === 400 &&
        data?.message === 'Please enter Valid phone number'
      ) {
        toast?.show({
          intent: 'error',
          heading: i18n.t('toastMessages.error'),
          description:
            err?.response?.data?.error?.message ||
            i18n.t('toastMessages.somethingWentWrong'),
          hideAfter: 3000,
        })
        return data
      }

      const { lockedTill, customerId, reason } =
        // eslint-disable-next-line no-prototype-builtins
        data.hasOwnProperty('actionData') && data.actionData

      if (lockedTill) {
        toast?.show({
          intent: 'error',
          heading: i18n.t('toastMessages.error'),
          description: reason,
          hideAfter: 3000,
        })
        return data
      }
      if (data?.action) {
        // setting customerId to store
        if (data?.actionData?.customerId) {
          setCustomerId(customerId)
        }

        let paramObject = {
          customerId: customerId || addCustomerId,
          screenName: variables.screenName,
          isDemoSale: variables.isDemoSale,
        }

        if (
          variables?.isVerifyOtpOnly &&
          states[data?.action] !== states.SHOW_RECOMMENDATION_SCREEN
        ) {
          navigation.navigate(states[data?.action], {
            ...paramObject,
            isReconsentFlow: variables?.isReconsentFlow,
            subagentId: variables?.subagentId,
            isExperianEnabled: data?.actionData?.isExperianEnabled,
          })
        } else if (
          isReconcentFlowOtpVerify &&
          states[data?.action] === states.SHOW_RECOMMENDATION_SCREEN
        ) {
          queryClient.invalidateQueries({ queryKey: ['home-widgets'] })
          queryClient.invalidateQueries(['customers'])
          navigation.navigate({
            name: states[data?.action],
            params: {
              isBureauReportExpired: false,
            },
            merge: true,
          })
        } else if (
          isReconcentFlowOtpVerify &&
          variables.screenName === 'BACK'
        ) {
          navigation.goBack()
          setIsReconcentFlowOtpVerify(false)
        } else if (isBrandsFlow && states[data?.action] === 'BrandPage') {
          navigation.replace('BrandSpecificRecommendationLoader', {
            customerId: customerId || addCustomerId,
            customerName: selectedCustomer?.name || '',
            customerPhoneNumber: selectedCustomer?.phoneNumber || '',
            brandId: partnerId,
          })
        } else {
          if (states[data?.action] === 'BrandPage') {
            paramObject = {
              customerId: customerId || addCustomerId,
              customerPhoneNumber: selectedCustomer?.phoneNumber || '',
              customerName: selectedCustomer?.name || '',
              showScratchCard: !variables?.phoneNumber,
              routeName: 'forCustomerFlow',
              isDemoSale: variables.isDemoSale,
            }
          }
          navigateToCustomerFlow(paramObject, data, variables)
        }
      }
      return data
    },
    onError: (err) => {
      toast?.show({
        intent: 'error',
        heading: i18n.t('toastMessages.error'),
        description:
          err?.response?.data?.error?.message ||
          i18n.t('toastMessages.somethingWentWrong'),
        hideAfter: 3000,
      })
    },
  })
}

const useAddCustomerNextStateWeb = () => {
  const {
    setCustomerId,
    customerId: addCustomerId,
    brandSpecificPartnerId,
    setIsWebFlow,
    customerName,
  } = useAddCustomerStore((store) => store)
  const isCreditScoreCheckFlow = getSessionData('isCreditScoreCheck') === 'true'

  const navigation = useNavigation()
  const toast = useToast()
  return useMutation(getNextStateAddCustomerState, {
    onSuccess: (data, variables) => {
      const isUserFlow = isUserFlowActive()

      if (data?.actionData?.error?.code === 2025) {
        toast?.show({
          intent: 'error',
          heading: i18n.t('toastMessages.error'),
          description:
            data?.actionData?.error?.message ||
            i18n.t('toastMessages.somethingWentWrong'),
          hideAfter: 3000,
        })
      }
      if (data?.code === 400 && data?.message === 'CAN_NOT_GO_BACK') {
        navigation.goBack()
        return data
      }

      if (
        data?.code === 400 &&
        data?.message === 'Please enter Valid phone number'
      ) {
        toast?.show({
          intent: 'error',
          heading: i18n.t('toastMessages.error'),
          description:
            err?.response?.data?.error?.message ||
            i18n.t('toastMessages.somethingWentWrong'),
          hideAfter: 3000,
        })
        return data
      }

      const { lockedTill, customerId, reason } =
        // eslint-disable-next-line no-prototype-builtins
        data.hasOwnProperty('actionData') && data.actionData

      //! This check for hash after phonenumber and name screen
      if (data?.actionData?.hash) {
        replaceLoginHash(data?.actionData?.hash)
      }
      if (isUserFlow) {
        setUserFlowSession({
          active: true,
          ...(variables?.fullName
            ? {
                fullName: variables.fullName,
              }
            : {}),
          ...(variables?.phoneNumber
            ? {
                phoneNumber: variables.phoneNumber?.toString(),
              }
            : {}),
        })
      }
      if (data?.lockedTill) {
        toast?.show({
          intent: 'error',
          heading: i18n.t('toastMessages.error'),
          description: data?.reason,
          hideAfter: 3000,
        })
      }

      if (lockedTill) {
        toast?.show({
          intent: 'error',
          heading: i18n.t('toastMessages.error'),
          description: reason,
          hideAfter: 3000,
        })
        return data?.actionData
      }
      if (data?.action) {
        if (data?.actionData?.customerId) {
          setCustomerId(customerId)
          if (isUserFlow) {
            setUserFlowSession({
              active: true,
              customerId,
            })
          }
        }
        if (variables?.isVerifyOtpOnly) {
          if (variables.screenName === 'SHOWN_PHONE_NUMBER_SCREEN') {
            if (!isUserFlow || data?.action === 'SHOW_OTP_SCREEN') {
              return false
            }
          } else {
            navigation.replace('ThankYouAfterVerify')
            return data?.actionData
          }
        }
        setIsWebFlow(true)
        if (webStates[data?.action] === 'VerifySecondaryPhoneNumber') {
          navigation.navigate(webStates[data?.action], {
            customerId: customerId || addCustomerId,
            maskedMobileNumbers: data?.maskedMobileNumbers,
            isExperianEnabled: data?.isExperianEnabled,
          })
          return data
        }

        if (
          variables?.screenName === 'GET_CURRENT_STATE' &&
          webStates[data?.action] === 'OtpScreen'
        ) {
          const requestBodyData = {
            fullName: variables.fullName || '',
            phoneNumber: variables.phoneNumber,
            screenName: 'SHOWN_PHONE_NUMBER_SCREEN',
            isReconsentFlow: variables?.isReconsentFlow,
          }

          getNextStateAddCustomerState(requestBodyData)
            .then((res) => {
              const { lockedTill, reason } =
                // eslint-disable-next-line no-prototype-builtins
                res.hasOwnProperty('actionData') && res.actionData

              if (res?.actionData?.error?.code === 2025) {
                toast?.show({
                  intent: 'error',
                  heading: i18n.t('toastMessages.error'),
                  description:
                    res?.actionData?.error?.message ||
                    i18n.t('toastMessages.somethingWentWrong'),
                  hideAfter: 3000,
                })
              }

              if (res?.code === 400 && res?.message === 'CAN_NOT_GO_BACK') {
                navigation.goBack()
                return res
              }

              if (lockedTill) {
                toast?.show({
                  intent: 'error',
                  heading: i18n.t('toastMessages.error'),
                  description: reason,
                  hideAfter: 3000,
                })
                return res?.actionData
              }
            })
            .catch((err) => {
              toast?.show({
                intent: 'error',
                heading: i18n.t('toastMessages.error'),
                description:
                  err?.response?.data?.error?.message ||
                  i18n.t('toastMessages.somethingWentWrong'),
                hideAfter: 3000,
              })
            })
        } else if (!variables?.isOtpLess) {
          if (data?.action === 'REDIRECTION_URL') {
            if (isUserFlow) {
              setUserFlowRoute('UserHome')
            }
            if (Platform.OS === 'web') {
              window.location.href = data?.actionData?.url
            } else {
              Linking.openURL(data?.actionData?.url)
            }
          } else {
            const nextRoute = isUserFlow
              ? (() => {
                  const currentRoute = webStates[data?.action]

                  if (currentRoute === 'OtpScreen') {
                    return 'UserOtp'
                  }

                  if (currentRoute === 'CustomerBasicDetailsV2') {
                    setUserFlowRoute('UserBasicDetails')
                    return 'UserBasicDetails'
                  }

                  if (currentRoute === 'PanDetailsV2') {
                    setUserFlowRoute('UserPanDetails')
                    return 'UserPanDetails'
                  }

                  if (currentRoute === 'BrandPage') {
                    const userFlowRoute = getUserFlowNextRoute()
                    setUserFlowRoute(userFlowRoute)
                    return userFlowRoute
                  }

                  return currentRoute
                })()
              : webStates[data?.action]

            const nextRouteParams = {
              customerId: customerId || addCustomerId,
              customerName: variables.fullName || customerName,
              customerPhoneNumber: variables.phoneNumber,
              isExperianEnabled: data?.actionData?.isExperianEnabled,
              brandSpecificPartnerId,
              ...(variables?.categoryId && {
                categoryId: variables.categoryId,
              }),
              ...(nextRoute === 'BrandPage' || nextRoute === 'UserHome'
                ? { isBureauReportExpired: false }
                : {
                    isReconsentFlow: variables?.isReconsentFlow,
                    screenName: variables?.screenName,
                  }),
              ...(getTagType() && { tag: getTagType() }),
            }

            if (isUserFlow && nextRoute === 'UserHome') {
              navigation.replace('UserHome', nextRouteParams)
              return data?.actionData
            }

            navigation.navigate(
              isCreditScoreCheckFlow && nextRoute === 'BrandPage'
                ? 'CreditReport'
                : nextRoute,
              nextRouteParams,
            )
          }
        }
      }

      return data?.actionData
    },
    onError: (err) => {
      toast?.show({
        intent: 'error',
        heading: i18n.t('toastMessages.error'),
        description:
          err?.response?.data?.error?.message ||
          i18n.t('toastMessages.somethingWentWrong'),
        hideAfter: 3000,
      })
    },
  })
}

export { useAddCustomerNextState, useAddCustomerNextStateWeb }
